import { useEffect, useState } from 'react';
import axios from 'axios';
import { FileDown, ShieldCheck, Lock, BadgeCheck, Briefcase } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * MoatsDownloadPanel — Physician-UI panel that surfaces all 8 legal-moat
 * download buttons, gated per-encounter by deterministic backend logic.
 *
 * Built 2026-06-08. Reads /api/moats/applicable/{encounterId} on mount and
 * renders one button per moat. Applicable moats get a clean primary button.
 * Non-applicable moats are shown as small disabled chips with the reason
 * as a tooltip — keeps the physician aware that the moat *exists* without
 * spamming irrelevant downloads.
 *
 * Moat A clicks are duplicated here as the canonical entry point. The
 * earlier in-card Moat A button (OffLabelAttestationCard) remains as a
 * secondary surface for the off-label-only workflow.
 */
export default function MoatsDownloadPanel({ encounterId }) {
  const [moats, setMoats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!encounterId) return;
    setLoading(true);
    axios.get(`${API}/api/moats/applicable/${encounterId}`)
      .then(r => { setMoats(r.data?.moats || []); setLoading(false); })
      .catch(e => {
        setError(e.response?.data?.error || e.message || 'Failed to load moat applicability');
        setLoading(false);
      });
  }, [encounterId]);

  const openMoatDoc = (moatId) => {
    const url = `${API}/api/moats/${moatId.toLowerCase()}/${encounterId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openInvestorBundle = () => {
    const url = `${API}/api/investor-bundle/${encounterId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <section
        className="bg-white rounded-lg border-2 border-slate-200 p-4"
        data-testid="moats-panel-loading"
      >
        <div className="text-xs text-slate-500 italic">Loading moat applicability…</div>
      </section>
    );
  }
  if (error || !moats) {
    return (
      <section
        className="bg-white rounded-lg border-2 border-slate-200 p-4"
        data-testid="moats-panel-error"
      >
        <div className="text-xs text-red-600">Moat panel unavailable: {error || 'no data'}</div>
      </section>
    );
  }

  const applicable = moats.filter(m => m.applicable);
  const notApplicable = moats.filter(m => !m.applicable);

  return (
    <section
      className="bg-white rounded-lg border-2 border-slate-200 p-4"
      data-testid="moats-download-panel"
    >
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-4 h-4 text-slate-700" />
        <h2 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
          Legal Moat Documents
        </h2>
        <span className="ml-auto text-[10px] text-slate-500">
          {applicable.length} applicable · {notApplicable.length} not applicable
        </span>
      </div>

      <p className="text-[11px] text-slate-600 mb-3 leading-relaxed">
        Deterministic, hash-chained documents generated from this encounter's
        clinical findings. Moat A is production-ready. Moats B–H are draft
        templates pending legal review per institutional policy.
      </p>

      {/* Applicable moats — primary download buttons */}
      <div className="space-y-2" data-testid="applicable-moats-list">
        {applicable.map(m => (
          <MoatRow
            key={m.moat_id}
            moat={m}
            onClick={() => openMoatDoc(m.moat_id)}
            applicable
          />
        ))}
        {applicable.length === 0 && (
          <div
            className="text-[11px] italic text-slate-500 px-2 py-3 bg-slate-50 rounded border border-slate-200"
            data-testid="no-applicable-moats"
          >
            No moats apply to this encounter. (Triggers: off-label medications,
            HIGH+ risk flags, DME-eligible ICD-10, controlled substances, etc.)
          </div>
        )}
      </div>

      {/* Investor Demo Bundle — one-click compile-everything button */}
      <div className="mt-4 pt-3 border-t border-slate-200" data-testid="investor-bundle-row">
        <button
          onClick={openInvestorBundle}
          data-testid="investor-bundle-button"
          className="w-full flex items-center justify-center gap-2 text-xs font-bold px-4 py-2.5 rounded bg-gradient-to-r from-indigo-700 to-blue-700 text-white border border-indigo-800 hover:from-indigo-800 hover:to-blue-800 transition-colors"
          title="Generate a single-file investor demo bundle: cover + platform snapshot + SOAP + risk flags + differential + Moat A + applicable-moats summary. Browser-printable to PDF."
        >
          <Briefcase className="w-4 h-4" />
          Generate Investor Demo Bundle (single HTML / printable PDF)
        </button>
        <div className="text-[10px] text-slate-500 mt-1 italic">
          Combines encounter SOAP + Moat A doc + platform snapshot + applicable
          moats summary into one downloadable file for Series A outreach.
        </div>
      </div>

      {/* Non-applicable moats — collapsed informational chips */}
      {notApplicable.length > 0 && (
        <details className="mt-4 pt-3 border-t border-slate-100" data-testid="non-applicable-moats">
          <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-700">
            Show {notApplicable.length} non-applicable moat(s) — informational only
          </summary>
          <div className="mt-2 space-y-1">
            {notApplicable.map(m => (
              <MoatRow
                key={m.moat_id}
                moat={m}
                onClick={() => openMoatDoc(m.moat_id)}
                applicable={false}
              />
            ))}
          </div>
        </details>
      )}
    </section>
  );
}


function MoatRow({ moat, onClick, applicable }) {
  if (applicable) {
    return (
      <button
        onClick={onClick}
        data-testid={`moat-${moat.moat_id.toLowerCase()}-download-button`}
        className="w-full flex items-start gap-3 text-left px-3 py-2 rounded border border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-slate-400 transition-colors group"
        title={moat.reason}
      >
        <div className={`shrink-0 mt-0.5 w-7 h-7 rounded flex items-center justify-center font-bold text-[11px] ${moat.production_ready ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-amber-50'}`}>
          {moat.moat_id}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-900 group-hover:text-slate-950">
              {moat.name}
            </span>
            {moat.production_ready && (
              <BadgeCheck className="w-3 h-3 text-emerald-600 shrink-0" />
            )}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5 leading-relaxed">
            {moat.reason}
          </div>
        </div>
        <FileDown className="w-4 h-4 text-slate-500 group-hover:text-slate-800 shrink-0 mt-0.5" />
      </button>
    );
  }
  return (
    <div
      data-testid={`moat-${moat.moat_id.toLowerCase()}-disabled-chip`}
      className="flex items-center gap-2 px-3 py-1.5 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded"
      title={moat.reason}
    >
      <Lock className="w-3 h-3 shrink-0" />
      <span className="font-semibold">Moat {moat.moat_id}</span>
      <span className="text-slate-400">·</span>
      <span className="truncate">{moat.reason}</span>
    </div>
  );
}
